import React from "react";
import LegalLayout, { LegalSection, LegalSub, LegalList, LegalCallout } from "../../components/LegalLayout";
import SEO from "../../components/SEO";
import { COMPANY } from "../../config/COMPANY-INFO";
import { useCookieConsent } from "../../contexts/CookieConsentContext";

export default function CookiesPage() {
  const { reopenSettings } = useCookieConsent();
  return (
    <>
      <SEO
        title="Cookies a lokální úložiště"
        description="Přehled cookies a lokálního úložiště, které Apex Detailing používá na webu, a jak si nastavení upravit."
        path="/pravni/cookies"
      />
      <LegalLayout
      title="Cookies a lokální úložiště"
      subtitle="Co používáme a proč. Stručně, ale beze zbytku."
      testId="page-cookies"
    >
      <LegalSection id="co-jsou" title="1. Co jsou cookies a lokální úložiště">
        <p>
          Cookies jsou malé textové soubory, které webové stránky ukládají ve vašem prohlížeči, aby si pamatovaly určitá nastavení mezi návštěvami (např. že jste přihlášeni nebo že jste si zavřeli upozornění).
          „LocalStorage“ je podobný mechanismus přímo součástí prohlížeče — funguje stejně, ale větší kapacitou a bez automatického odesílání na server.
        </p>
      </LegalSection>

      <LegalSection id="co-pouzivame" title="2. Co konkrétně používáme">
        <p>
          Nepoužíváme Google Analytics ani Meta Pixel. Používáme jen měřicí značku <strong>Google Ads</strong> pro vyhodnocení účinnosti reklamy — a to pouze tehdy, pokud k tomu udělíte souhlas.
          Pracujeme s těmito položkami:
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="cookies-table">
            <thead>
              <tr className="text-apex-gold uppercase tracking-wider text-[11px]">
                <th className="text-left py-2 pr-4 border-b border-apex-line">Položka</th>
                <th className="text-left py-2 pr-4 border-b border-apex-line">Typ</th>
                <th className="text-left py-2 pr-4 border-b border-apex-line">Účel</th>
                <th className="text-left py-2 border-b border-apex-line">Životnost</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-apex-line/40">
                <td className="py-3 pr-4 font-semibold text-apex-cream font-mono text-xs">apex_token</td>
                <td className="py-3 pr-4">Nezbytná (localStorage)</td>
                <td className="py-3 pr-4 text-apex-cream/75">Přihlašovací token (JWT) — bez něj nemůžete být přihlášeni.</td>
                <td className="py-3 text-apex-cream/65">7 dní</td>
              </tr>
              <tr className="border-b border-apex-line/40">
                <td className="py-3 pr-4 font-semibold text-apex-cream font-mono text-xs">apex_seen_rejected_v1</td>
                <td className="py-3 pr-4">Funkční (localStorage)</td>
                <td className="py-3 pr-4 text-apex-cream/75">Pamatuje si, která upozornění v zvonečku jste již zavřeli.</td>
                <td className="py-3 text-apex-cream/65">trvale (dokud nesmažete)</td>
              </tr>
              <tr className="border-b border-apex-line/40">
                <td className="py-3 pr-4 font-semibold text-apex-cream font-mono text-xs">apex_seen_pay_v1</td>
                <td className="py-3 pr-4">Funkční (localStorage)</td>
                <td className="py-3 pr-4 text-apex-cream/75">Stejné jako výše — pro upomínky o platbě.</td>
                <td className="py-3 text-apex-cream/65">trvale</td>
              </tr>
              <tr className="border-b border-apex-line/40">
                <td className="py-3 pr-4 font-semibold text-apex-cream font-mono text-xs">_gcl_au / Google Ads Tag</td>
                <td className="py-3 pr-4">Marketingové / reklamní</td>
                <td className="py-3 pr-4 text-apex-cream/75">Měřicí značka <strong>Google Ads</strong> (ID AW-18426007268), poskytovatel Google — měří konverze z reklamních kampaní (zda návštěva webu vedla k odeslání rezervace). Načte se výhradně po udělení souhlasu s kategorií „Marketing“.</td>
                <td className="py-3 text-apex-cream/65">do 90 dní</td>
              </tr>
            </tbody>
          </table>
        </div>
        <LegalCallout tone="info">
          Nezbytné a funkční položky výše zůstávají pouze ve vašem prohlížeči a nikam je neodesíláme. Značka Google Ads je jediná výjimka — odešle údaje o návštěvě Google, ale pouze pokud jí to v cookie liště povolíte.
        </LegalCallout>
      </LegalSection>

      <LegalSection id="souhlas" title="3. Souhlas a jak ho udělit / odvolat">
        <p>
          Tento web ukládá pouze <strong>nezbytné a funkční</strong> položky. Při první návštěvě se vám zobrazí cookie lišta, ve které si zvolíte, zda chcete povolit i nepovinné funkční položky (mapa Google, zapamatování zavřených upozornění). Volba je vázána pouze k vašemu prohlížeči.
        </p>
        <p>
          <strong>Volbu můžete kdykoliv změnit nebo odvolat</strong> — buď tlačítkem níže, nebo přes odkaz „Nastavení cookies" v patičce webu. Po odvolání souhlasu nepovinné položky okamžitě smažeme z vašeho prohlížeče.
        </p>
        <div className="not-prose mt-4">
          <button
            type="button"
            onClick={reopenSettings}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-apex-gold text-apex-bg text-sm font-bold hover:bg-apex-gold-soft transition-colors"
            data-testid="cookies-page-open-settings"
          >
            Otevřít nastavení cookies
          </button>
        </div>
      </LegalSection>

      <LegalSection id="vypnuti" title="4. Jak cookies vypnout / smazat">
        <p>Cookies a lokální úložiště lze kdykoliv vymazat přímo v nastavení vašeho prohlížeče. Po smazání budete odhlášeni a předvolby budou resetovány na výchozí hodnoty.</p>
        <LegalList>
          <li><strong>Chrome:</strong> Nastavení → Soukromí a zabezpečení → Cookies a další data webů</li>
          <li><strong>Firefox:</strong> Nastavení → Soukromí a zabezpečení → Cookies a data stránek</li>
          <li><strong>Safari:</strong> Předvolby → Soukromí → Spravovat data webů</li>
          <li><strong>Edge:</strong> Nastavení → Soukromí, vyhledávání a služby → Vymazat data prohlížení</li>
        </LegalList>
      </LegalSection>

      <LegalSection id="souvislost" title="5. Související dokumenty">
        <p>
          Více o tom, jak nakládáme s vašimi osobními údaji, najdete v dokumentu{" "}
          <a href="/pravni/ochrana-osobnich-udaju" className="text-apex-gold hover:underline">Zásady ochrany osobních údajů (GDPR)</a>.
        </p>
        <p className="text-sm text-apex-cream/65">
          Provozovatel: <strong>{COMPANY.legalName}</strong> · {COMPANY.address.full} · <a href={`mailto:${COMPANY.contact.email}`} className="text-apex-gold hover:underline">{COMPANY.contact.email}</a>
        </p>
        <LegalCallout>
          Účinnost: <strong>{COMPANY.effectiveDate}</strong>.
        </LegalCallout>
      </LegalSection>
    </LegalLayout>
    </>
  );
}
